import React from "react";
import { Cube, Pulse, FilePlus, Fire } from "@phosphor-icons/react";
import { cn, formatBigCount, formatRelativeTime } from "@/lib/format";
import { fmtInt, windowDelta, avgDelta, VsPrevious } from "@/components/assets/parts";

const N_FOR = { "24h": 1, "7d": 7, "30d": null };

const Card = ({ icon: Icon, label, value, desc, updated, footer, tone = "cyan", testid, loading }) => (
  <div
    data-testid={testid}
    className="group relative overflow-hidden rounded-2xl border border-primary/20 bg-card/40 backdrop-blur-md p-4 sm:p-5 box-glow transition-colors hover:border-primary/40"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">{label}</span>
      {updated ? <span className="text-[9px] uppercase tracking-[0.12em] text-muted-foreground/60 mono whitespace-nowrap">updated {updated}</span> : null}
    </div>
    <div className="mt-2 flex items-center justify-between gap-3">
      {loading ? (
        <span className="inline-block h-8 w-28 bg-primary/10 animate-pulse rounded" />
      ) : (
        <span
          className={cn(
            "font-heading font-bold leading-none tabnum text-[28px] sm:text-[34px]",
            tone === "red" ? "text-rose-300" : tone === "amber" ? "text-amber-200" : "text-[#22d3ee] text-glow-cyan",
          )}
        >
          {value}
        </span>
      )}
      {Icon ? (
        <Icon
          size={30}
          weight="duotone"
          className={cn(
            "shrink-0 transition-transform group-hover:scale-110",
            tone === "red" ? "text-rose-400/70" : tone === "amber" ? "text-amber-300/70" : "text-primary/70",
          )}
        />
      ) : null}
    </div>
    {desc ? <div className="mt-1.5 text-[11px] text-muted-foreground/80 leading-snug max-w-[22ch]">{desc}</div> : null}
    <div className="mt-3 pt-2.5 border-t border-primary/10 min-h-[22px]">{footer}</div>
  </div>
);

export const AssetMetricCards = ({ stats, daily, win, loading }) => {
  const n = N_FOR[win];
  const total = stats?.total_assets;
  const active = stats?.active_assets?.[win];
  const neu = stats?.new_assets?.[win];
  const mb = stats?.mint_burn?.[win] || {};
  const updated = stats?.freshness?.block_time ? formatRelativeTime(stats.freshness.block_time) : null;

  // Additive deltas (accurate) for new assets + mint/burn events.
  const newD = windowDelta(daily, "new_assets", n);
  const mintD = windowDelta(daily, "mint_events", n);
  const burnD = windowDelta(daily, "burn_events", n);
  const mbDelta = mintD && burnD ? { pct: mintD.prev + burnD.prev === 0 ? null : (((mintD.cur + burnD.cur) - (mintD.prev + burnD.prev)) / (mintD.prev + burnD.prev)) * 100, delta: (mintD.cur + burnD.cur) - (mintD.prev + burnD.prev) } : null;
  // Average daily distinct active assets (active_assets is not additive).
  const activeD = avgDelta(daily, "active_assets", n);

  const totalPct = total && total.baseline ? (total.added_since_baseline / total.baseline) * 100 : null;

  return (
    <section data-testid="asset-metric-cards" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
      <Card
        testid="metric-total"
        icon={Cube}
        label="Total native assets"
        value={total ? formatBigCount(total.value) : "—"}
        desc="All native assets ever created on Cardano (unique policy + name)."
        updated={updated}
        loading={loading && !stats}
        footer={<VsPrevious testid="metric-total-delta" label="since baseline" pct={totalPct} delta={total?.added_since_baseline} />}
      />
      <Card
        testid="metric-active"
        icon={Pulse}
        label="Active assets"
        value={active != null ? fmtInt(active) : "—"}
        desc={`Assets with on-chain activity in the last ${win}.`}
        updated={updated}
        loading={loading && !stats}
        footer={activeD ? <VsPrevious testid="metric-active-delta" label={`vs previous ${win}`} pct={activeD.pct} /> : <span data-testid="metric-active-delta" className="text-[10px] mono text-muted-foreground/60">daily avg trend</span>}
      />
      <Card
        testid="metric-new"
        icon={FilePlus}
        label="New assets"
        value={neu != null ? fmtInt(neu) : "—"}
        desc={`Unique assets first minted in the last ${win}.`}
        updated={updated}
        loading={loading && !stats}
        footer={newD ? <VsPrevious testid="metric-new-delta" label={`vs previous ${win}`} pct={newD.pct} delta={newD.delta} /> : <span data-testid="metric-new-delta" className="text-[10px] mono text-muted-foreground/60">no prior period</span>}
      />
      <Card
        testid="metric-mintburn"
        icon={Fire}
        tone="amber"
        label="Mint / burn events"
        value={<span>{fmtInt(mb.mint_events)}<span className="text-muted-foreground/50"> / </span><span className="text-rose-300">{fmtInt(mb.burn_events)}</span></span>}
        desc={`Total mint and burn transactions in the last ${win}.`}
        updated={updated}
        loading={loading && !stats}
        footer={mbDelta ? <VsPrevious testid="metric-mintburn-delta" label={`vs previous ${win}`} pct={mbDelta.pct} delta={mbDelta.delta} /> : <span data-testid="metric-mintburn-delta" className="text-[10px] mono text-muted-foreground/60">no prior period</span>}
      />
    </section>
  );
};
